// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { contactSchema } from '@/contact/api/types';
import { itemFormSchema } from '@/orders/schemas/item';
import { sparseLocationSchema, sparseProjectSchema } from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

const contactFormValueSchema = contactSchema.pick({
  id: true,
  firstName: true,
  lastName: true,
  email: true,
  phone: true,
  customer: true,
});

/** Maximum number of line items allowed on a fulfillment order. Shared
 * between the Zod schema (`.max()`) and the UI's "Add item" enable gate so
 * both sides agree. */
export const FULFILLMENT_ORDER_MAX_ITEMS = 50;

// Extend the shared schema with item-level validation for FO
const foItemSchema = itemFormSchema.superRefine((data, ctx) => {
  if (data.item === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Item is required',
      path: ['item'],
    });
  }
  if (data.quantity === null || data.quantity <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Quantity must be greater than 0',
      path: ['quantity'],
    });
  }
});

export const fulfillmentOrderFormSchema = z
  .object({
    project: sparseProjectSchema.nullable(),
    fromLocation: sparseLocationSchema.nullable(),
    toLocation: sparseLocationSchema.nullable(),
    promiseShipDate: dateSchema.nullable(),
    promiseDeliveryDate: dateSchema.nullable(),
    orderNotes: z.string().nullable(),
    contact: contactFormValueSchema.nullable(),
    items: z
      .array(foItemSchema)
      .min(1, { message: 'At least one item is required' })
      .max(FULFILLMENT_ORDER_MAX_ITEMS),
  })
  .superRefine((data, ctx) => {
    // Validate from location is required
    if (!data.fromLocation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'From location is required',
        path: ['fromLocation'],
      });
    }

    // Validate to location is required
    if (!data.toLocation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'To location is required',
        path: ['toLocation'],
      });
    }
  });

export type FulfillmentOrderFormData = z.infer<
  typeof fulfillmentOrderFormSchema
>;
